import React, { useState, createRef, useEffect, useContext } from "react"
import styled, { css } from "styled-components"

// Types
import { IEvent } from "../types/events"

// Context
import Context from "../context/context"

// Sounds
import cardFlipSound from "../images/cardflip.mp3"
import crowdSound from "../images/crowd.mp3"
import airHornSound from "../images/airhorn.mp3"
import drumRollSound from "../images/drumroll.mp3"

// Styling
import textStyles from "../styles/textStyles"
import colors from "../styles/colors"
import { fastDuration } from "../styles/animations"

// Component
import Icon from "../components/icons/Icon"

interface IProps {
  event: IEvent
  open: boolean
  closeModal: () => void
}

const ModalCard = ({ event, open, closeModal }: IProps) => {
  const cardFlipSoundRef = createRef<HTMLAudioElement>()
  const crowdSoundRef = createRef<HTMLAudioElement>()
  const airHornSoundRef = createRef<HTMLAudioElement>()
  const drumRollSoundRef = createRef<HTMLAudioElement>()

  const [cardFlipped, setCardFlipped] = useState<boolean>(false)

  const app = useContext(Context)

  const handleCardFlip = () => {
    if (cardFlipSoundRef.current && drumRollSoundRef.current) {
      cardFlipSoundRef.current.play()
      drumRollSoundRef.current.pause()
      drumRollSoundRef.current.currentTime = 0
    }
    if (app) {
      app.updateParty(true)
    }
    setCardFlipped(true)
  }

  const handleCloseModal = () => {
    closeModal()
    setCardFlipped(false)
    if (crowdSoundRef.current && airHornSoundRef.current) {
      crowdSoundRef.current.pause()
      crowdSoundRef.current.currentTime = 0
      airHornSoundRef.current.pause()
      airHornSoundRef.current.currentTime = 0
    }
    if (app) {
      app.updateParty(false)
    }
  }

  const handleCheering = () => {
    if (cardFlipped === true) {
      if (crowdSoundRef.current && airHornSoundRef.current) {
        airHornSoundRef.current.play()
        crowdSoundRef.current.play()
      }
    }
  }

  useEffect(() => {
    handleCheering()
  }, [cardFlipped])

  useEffect(() => {
    if (open && drumRollSoundRef.current) {
      drumRollSoundRef.current.play()
    }
  }, [open])

  return (
    <Container open={open}>
      <audio ref={cardFlipSoundRef}>
        <source src={cardFlipSound} type="audio/mpeg" />
      </audio>
      <audio ref={crowdSoundRef}>
        <source src={crowdSound} type="audio/mpeg" />
      </audio>
      <audio ref={airHornSoundRef}>
        <source src={airHornSound} type="audio/mpeg" />
      </audio>
      <audio ref={drumRollSoundRef} loop>
        <source src={drumRollSound} type="audio/mpeg" />
      </audio>
      <CardWrapper>
        <Card cardFlipped={cardFlipped}>
          <CardFront onClick={() => handleCardFlip()}>
            <CardFrontCorner>{event.day}</CardFrontCorner>
            <FrontContent>
              <FrontContentMain>
                <Icon
                  icon="tree"
                  color={colors.yellow}
                  width={100}
                  height={100}
                  direction="right"
                />
                <FrontTitle>Click</FrontTitle>
                <Icon
                  icon="tree"
                  color={colors.yellow}
                  width={100}
                  height={100}
                  direction="left"
                />
              </FrontContentMain>
              <FrontSubtitle>to see your surprise</FrontSubtitle>
            </FrontContent>
          </CardFront>
          <CardBack>
            <CardBackCorner onClick={() => handleCloseModal()}>
              X
            </CardBackCorner>
            <Image
              img={event.image.url}
              role="img"
              aria-label={event.image.alt || event.title}
            />
            <BackContent>
              <Title>{event.title}</Title>
              <div
                dangerouslySetInnerHTML={{ __html: event.description.html }}
              />
            </BackContent>
          </CardBack>
        </Card>
      </CardWrapper>
    </Container>
  )
}

const Container = styled.div`
  z-index: 2;
  display: ${({ open }: { open: boolean }) => (open ? "flex" : "none")};
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  padding: 24px;
  background-color: rgba(0, 0, 0, 0.4);
  overflow: scroll;
`

const CardWrapper = styled.div`
  @keyframes cardAppear {
    0% {
      transform: rotate3d(0, 0, 1, 15deg) translate3d(0, -100vh, 0);
    }
    30% {
      transform: rotate3d(0, 0, 0, 0deg) translate3d(0, 0, 0);
    }
    65% {
      transform: rotate3d(0, 0, 0, 0deg) translate3d(0, 0, 0) scale3d(1, 1, 1);
    }
    80% {
      transform: rotate3d(0, 0, 1, -5deg) scale3d(1.05, 1.05, 1);
    }
  }

  width: 400px;
  height: 600px;
  margin: auto;
  animation: cardAppear 1.6s forwards;
`

const Card = styled.div`
  @keyframes cardFlip {
    0% {
      transform: rotateZ(0deg) rotateY(180deg);
    }
    50% {
      transform: rotateZ(-10deg) rotateY(90deg);
    }
    100% {
      transform: rotateZ(0deg) rotateY(0deg);
    }
  }

  @keyframes cardUnflip {
    0% {
      transform: rotateZ(0deg) rotateY(0deg);
    }
    50% {
      transform: rotateZ(-10deg) rotateY(90deg);
    }
    100% {
      transform: rotateZ(0deg) rotateY(180deg);
    }
  }

  width: 100%;
  height: 100%;
  transform-style: preserve-3d;
  ${({ cardFlipped }: { cardFlipped: boolean }) =>
    cardFlipped
      ? `animation: cardFlip ${fastDuration}ms forwards linear`
      : `animation: cardUnflip ${fastDuration}ms forwards linear`};
`

const cardStyling = css`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  background-image: url("data:image/png;base64,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");
  background-color: ${colors.red};
`

const CardFront = styled.div`
  cursor: pointer;
  ${cardStyling};
  justify-content: center;
  width: 100%;
  transform: rotateY(180deg);
`

const CardFrontCorner = styled.div`
  ${textStyles.cardCornerTitle};
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: -20px;
  left: -20px;
  width: 80px;
  height: 80px;
  background-image: url("data:image/png;base64,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");
  background-color: ${colors.red};
  box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.5);
`

const FrontContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  width: 100%;
  height: 100%;
`

const FrontContentMain = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`

const FrontTitle = styled.h3`
  ${textStyles.frontCardTitle};
`

const FrontSubtitle = styled.h4`
  ${textStyles.frontCardSubtitle};
`

const CardBack = styled.div`
  ${cardStyling};
  transform: rotateY(0deg);
`

const CardBackCorner = styled.div`
  cursor: pointer;
  ${textStyles.cardCornerTitle};
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: -20px;
  right: -20px;
  width: 80px;
  height: 80px;
  background-image: url("data:image/png;base64,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");
  background-color: ${colors.red};
  box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.5);
`

const Image = styled.div`
  width: 100%;
  height: 200px;
  ${({ img }: { img: string }) => img && `background-image: url(${img})`};
  background-size: cover;
  background-position: center;
`

const BackContent = styled.div`
  padding: 24px;
`

const Title = styled.h2`
  ${textStyles.title};
`

export default ModalCard
